import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MonitorIcon from '@mui/icons-material/Monitor';
import RouterIcon from '@mui/icons-material/Router';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, List, Stack, Typography } from '@mui/material';

import DeviceImage from '../Avatars/DeviceImage';
import { SectionHeader } from '../Layout/SectionHeader';
import BasicModal from './BasicModal';

import type { DevicespecsDTO, UserUIDeviceGroupResponseDTO } from 'gen/model';

const deviceInfoCategories = [
    {
        category: 'General',
        icon: SmartphoneIcon,
        items: [
            { key: 'released', label: 'Released' },
            { key: 'modelNumber', label: 'Model Number' },
            { key: 'manufacturer', label: 'Manufacturer' },
            { key: 'deviceCategory', label: 'Device Category' },
        ],
    },
    {
        category: 'System',
        icon: SettingsIcon,
        items: [
            { key: 'operatingSystemAdditionalInfo', label: 'Operating System Additional Info' },
            { key: 'cpu', label: 'CPU' },
            { key: 'ramCapacity', label: 'RAM Capacity' },
            { key: 'nonVolatileMemoryCapacity', label: 'Non-Volatile Memory Capacity' },
            { key: 'graphicalController', label: 'Graphical Controller' },
        ],
    },
    {
        category: 'Display',
        icon: MonitorIcon,
        items: [
            { key: 'displayDiagonal', label: 'Display Diagonal' },
            { key: 'secondaryDisplayDiagonal', label: 'Secondary Display Diagonal' },
            { key: 'secondaryDisplayResolution', label: 'Secondary Display Resolution' },
            { key: 'touchscreenType', label: 'Touchscreen Type' },
        ],
    },
    {
        category: 'Connectivity',
        icon: RouterIcon,
        items: [
            { key: 'supportedCellularBands', label: 'Supported Cellular Bands' },
            { key: 'supportedCellularDataLinks', label: 'Supported Cellular Data Links' },
            { key: 'secondarySupportedCellularNetworks', label: 'Secondary Supported Cellular Networks' },
            { key: 'secondarySupportedCellularDataLinks', label: 'Secondary Supported Cellular Data Links' },
            { key: 'expansionInterfaces', label: 'Expansion Interfaces' },
            { key: 'bluetooth', label: 'Bluetooth' },
            { key: 'wirelessLAN', label: 'Wireless LAN' },
        ],
    },
    {
        category: 'Camera',
        icon: CameraAltIcon,
        items: [
            { key: 'cameraResolution', label: 'Camera Resolution' },
            { key: 'numberOfEffectivePixels', label: 'Number of Effective Pixels' },
            { key: 'auxiliaryCameraNumberOfPixels', label: 'Auxiliary Camera Number of Pixels' },
            { key: 'auxiliary2CameraNumberOfPixels', label: 'Auxiliary 2nd Camera Number of Pixels' },
            { key: 'secondaryCameraResolution', label: 'Secondary Camera Resolution' },
            { key: 'secondaryCameraNumberOfPixels', label: 'Secondary Camera Number of Pixels' },
        ],
    },
    {
        category: 'Battery',
        icon: BatteryChargingFullIcon,
        items: [{ key: 'nominalBatteryCapacity', label: 'Nominal Battery Capacity' }],
    },
];

interface DeviceInformationModalProps {
    deviceGroup: UserUIDeviceGroupResponseDTO;
    deviceSpec: DevicespecsDTO | undefined;
    handleClose: () => void;
    open: boolean;
}

export default function DeviceInformationModal({
    deviceGroup,
    deviceSpec,
    handleClose,
    open,
}: Readonly<DeviceInformationModalProps>) {
    const infoObj = { ...deviceSpec } as Record<string, string | undefined>;
    delete infoObj.id;
    delete infoObj.image;
    delete infoObj.resolution;
    delete infoObj.model;
    delete infoObj.brand;
    delete infoObj.operatingSystem;

    const categoryHasValues = (categoryItems: { key: string; label: string }[]) => {
        return categoryItems.some((item) => Boolean(infoObj[item.key]));
    };

    return (
        <BasicModal handleClose={handleClose} open={open} title="Technical Information">
            <Stack
                sx={{
                    height: { sm: '100%', xs: 'calc(100dvh - 60px)' },
                    maxHeight: { sm: '75dvh', xs: 'calc(100dvh - 60px)' },
                    pb: 4,
                }}
            >
                <Divider />
                <Stack
                    direction={{ sm: 'row', xs: 'column' }}
                    sx={{
                        backgroundColor: '#edf2f6',
                        p: 2,
                    }}
                >
                    <Stack
                        sx={{
                            alignItems: 'center',
                            pr: { sm: 1, xs: 0 },
                            pt: { sm: 1, xs: 0 },
                            width: { sm: 200, xs: '90vw' },
                        }}
                    >
                        <DeviceImage imageName={deviceSpec?.image} size={150} />
                    </Stack>
                    <Stack
                        sx={{
                            flex: 1,
                        }}
                    >
                        <SectionHeader description={deviceSpec?.brand} small title="Brand" />
                        <SectionHeader description={deviceSpec?.model} small title="Model" />
                        <SectionHeader description={deviceSpec?.operatingSystem} small title="Operating System" />
                        <SectionHeader description={deviceSpec?.resolution} small title="Screen Resolution" />
                    </Stack>
                </Stack>

                <Box sx={{ height: '100%', overflow: 'auto', px: 2, py: 2 }}>
                    <Typography sx={{ mb: 2 }} variant="body1">
                        <strong>Installation ID:</strong> {deviceGroup.installation_id ?? '-'}
                    </Typography>

                    {deviceInfoCategories.map((category) =>
                        categoryHasValues(category.items) ? (
                            <Accordion key={category.category}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ alignItems: 'center', display: 'flex' }}>
                                        <category.icon
                                            fontSize="small"
                                            sx={{
                                                color: 'primary.main',
                                                mr: 1,
                                            }}
                                        />
                                        <strong>{category.category}</strong>
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <List dense sx={{ pt: 0 }}>
                                        {category.items.map((item) =>
                                            infoObj[item.key] ? (
                                                <Typography key={item.key} sx={{ mb: 1 }} variant="body2">
                                                    • <strong>{item.label}:</strong> {infoObj[item.key]}
                                                </Typography>
                                            ) : null,
                                        )}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        ) : null,
                    )}
                </Box>
            </Stack>
        </BasicModal>
    );
}
